import React from "react";
import { Spinner } from "@/components/ui/spinner";
import { Link } from "react-router-dom";
import {
  ReplyAllIcon,
  BookCheckIcon,
  SendIcon,
  LanguagesIcon,
  ChevronUpIcon,
  InfoIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useAuth } from "@clerk/react";
import { useCreatorsStore, useInboxStore } from "@/stores";

interface InboxFooterProps {
  creator_id: string;
  /** Amy-run campaign → the reply is sent from (and the thread re-read from) Amy's pinned mailbox. */
  isEve?: boolean;
}

export default function InboxFooter({ creator_id, isEve }: InboxFooterProps) {
  const { getToken } = useAuth();
  const { creators } = useCreatorsStore();
  const { inbox, loadingManualSend, manualSend, fetchInbox } = useInboxStore();

  const creator = creators.find((c) => c._id === creator_id);
  const isHandOff = creator?.hand_off_to_user || false;
  const nextMessage = creator?.next_message;

  const [showTextArea, setShowTextArea] = React.useState(false);
  const [manualReplyContent, setManualReplyContent] = React.useState("");

  const handleManualSend = async (translate: boolean = false) => {
    const token = await getToken();
    if (!token) return;

    if (manualReplyContent.trim() === "") {
      toast.error("Reply content cannot be empty.");
      return;
    }

    try {
      await manualSend(
        creator_id,
        manualReplyContent,
        inbox?.[0]?.subject || "Re: ",
        token,
        translate,
        { isEve }
      );
      toast.success("Email sent successfully!");
      await fetchInbox(creator_id, token, { isEve });
      setManualReplyContent("");
      setShowTextArea(false);
    } catch (error) {
      toast.error((error as Error).message || "Error sending email.");
      console.error("Error sending email:", error);
    }
  };

  if (nextMessage) {
    return <div className="-mt-13"></div>;
  }

  if (isHandOff) {
    return (
      <div className="min-h-16 w-full flex flex-row justify-end px-2 text-xs text-muted-foreground items-center -mt-12">
        <Link to={`/dashboard/tasks?action=${creator_id}`}>
          <Button variant="neutral">
            <BookCheckIcon />
            Handle in Tasks
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-16 w-full gap-2 flex flex-col justify-end text-xs items-center sticky z-10 bottom-0">
      {showTextArea ? (
        <div className="w-full flex flex-col gap-3 bg-background/20 backdrop-blur-xs p-4 rounded-b-md border-t relative animate-in fade-in">
          <Label htmlFor="manual-reply-textarea">Reply manually:</Label>
          <Textarea
            value={manualReplyContent}
            readOnly={loadingManualSend}
            placeholder="Reply manually here..."
            onChange={(e) => setManualReplyContent(e.target.value)}
            className="min-h-28 max-h-96 relative bg-sidebar pb-12 resize-none"
            id="manual-reply-textarea"
            autoFocus
          />
          <div className="flex flex-row items-center justify-between absolute bottom-6 right-6 w-full gap-2">
            <span className="text-muted-foreground mr-auto ml-14.5">
              <InfoIcon className="size-3 mr-1.5 inline-block" />
              Replies All
            </span>
            <Button
              variant="secondary"
              onClick={() => setShowTextArea(false)}
              disabled={loadingManualSend}
              className="hover:bg-muted-foreground/20 bg-muted-foreground/10"
              size="sm"
            >
              Cancel
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild disabled={loadingManualSend}>
                <Button disabled={loadingManualSend} size="sm">
                  Send Email
                  {!loadingManualSend && <ChevronUpIcon />}
                  {loadingManualSend && (
                    <Spinner />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                side="top"
                align="end"
              >
                <DropdownMenuItem
                  className="text-xs h-8"
                  onClick={() => handleManualSend(false)}
                  disabled={loadingManualSend}
                >
                  <SendIcon className="size-3.5" /> Send as is
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-xs h-8"
                  onClick={() => handleManualSend(true)}
                  disabled={loadingManualSend}
                >
                  <LanguagesIcon /> Translate & Send
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      ) : (
        <div className="p-2 flex justify-end w-full sticky bottom-0 z-10">
          <Button
            onClick={() => {
              setShowTextArea(true);
              setManualReplyContent("");
            }}
          >
            <ReplyAllIcon /> Reply Manually
          </Button>
        </div>
      )}
    </div>
  );
}
